import { Fragment, useCallback, useEffect, useImperativeHandle, useRef } from "react"
import { Link } from "react-router-dom"
import { SiteHeader } from "@gogoro/go-design"
import classnames from "classnames"
import { useLanguages } from "lang/languages"
import { environment } from "modules/environments"
import { useSite } from "modules/sites"
import { useUser } from "modules/users"
import UserPanel from "views/User/UserPanel"

import Icon from "components/Icon"
import LanguageSelector from "components/LanguageSelector/LanguageSelector"

import styles from "./Masthead.module.scss"

const Masthead = ({ className, handleRef, scrollTopRef }) => {
  const userPanelRef = useRef()
  const barNodeRef = useRef()
  const hideAnimationRef = useRef()
  const summoned = useRef(false)
  const user = useUser()
  const site = useSite()
  const { languages, switchLanguage, language } = useLanguages()

  const languagesAction = languages.map((lang) => ({ label: lang.name, onClick: () => switchLanguage(lang.code) }))

  const openUserPanel = useCallback(() => userPanelRef.current.open(), [])

  useEffect(() => {
    if (site.clientCode === "sales_portal") {
      hideAnimationRef.current = barNodeRef.current.animate(
        [{ transform: "translateY(0)" }, { transform: "translateY(-100%)" }],
        {
          duration: 250,
          fill: "forwards",
          easing: "ease-in-out",
        },
      )
      hideAnimationRef.current.pause()
    }
    const revealBeforePrint = () => handleRef.current.reveal()
    window.addEventListener("beforeprint", revealBeforePrint)
    return () => {
      document.querySelector("#root").style.removeProperty("--header-position")
      window.removeEventListener("beforeprint", revealBeforePrint)
    }
  }, [handleRef, site.clientCode])

  useImperativeHandle(handleRef, () => ({
    hide: () => {
      const hideAnimation = hideAnimationRef.current
      if (hideAnimation?.currentTime === 0 || hideAnimation?.playbackRate === -1) {
        hideAnimation.playbackRate = 1
        hideAnimation.play()
        document.querySelector("#root").style.setProperty("--header-position", "-50px")
      }
    },
    reveal: (options) => {
      const hideAnimation = hideAnimationRef.current
      if (hideAnimation?.currentTime === 250 || hideAnimation?.playbackRate === 1) {
        hideAnimation.playbackRate = -1
        hideAnimation.play()
        document.querySelector("#root").style.setProperty("--header-position", "0px")
        summoned.current = options?.summoned
      }
    },
  }))

  const handleReveal = (event) => {
    if (scrollTopRef.current > 50) {
      handleRef.current.reveal({ summoned: true })
    }
  }

  const handleHide = (event) => {
    if (summoned.current && scrollTopRef.current > 50) {
      handleRef.current.hide()
    }
  }

  return (
    <header
      ref={barNodeRef}
      className={classnames(["service", "dashboard"].includes(site.name) ? styles.amulet : styles.container, className)}
      onMouseEnter={handleReveal}
      onMouseLeave={handleHide}>
      {["service", "dashboard"].includes(site.name) ? (
        <SiteHeader
          title={site.title}
          language={language}
          languages={languages}
          user={{ name: user.UserDisplayName || user.employeeName }}
          onChangeLanguage={switchLanguage}
        />
      ) : (
        <Fragment>
          <h1 className={styles.title}>
            <Link to="/">{site.title}</Link>
          </h1>
          <div
            className={classnames(
              styles.customSiteHeader,
              ["staging", "production"].includes(environment) && styles.languageHide,
            )}>
            <LanguageSelector actions={languagesAction} />
            <div className={styles.user} onClick={openUserPanel}>
              <span className={styles.department}>{user.StoreName || user.departmentName}</span>
              <span className={styles.name}>{user.UserDisplayName || user.employeeName}</span>
              <Icon className={styles.icon} icon="chevron-down" />
            </div>
          </div>

          <UserPanel ref={userPanelRef} />
        </Fragment>
      )}
    </header>
  )
}

export default Masthead
